import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {Entity} from "./entity";
import {ObjectPreferenceArrayCodec, ObjectPreferenceEntity} from "./object-preference";
import {WaterGoPreferenceCodec, WaterGoPreferenceEntity} from "./water-go-preference";
import {OilGoPreferenceCodec, OilGoPreferenceEntity} from "./oil-go-preference";
import {validateJSON, validateJSONNow} from "../web/validation";
import {PatchValue} from "./patch-value";
import {ProductionAllocationMethod, toProductionAllocationMethod} from "./production-allocation-method";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";

/** Validator for service type types. */
export const ServiceTypeValidator = t.intersection([
  t.type({
    id: t.string,
    type: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    createDttm: t.number,
    lastModifiedDttm: t.number,
    description: t.string,
    category: t.string,
    productionAllocationMethod: t.string,
    autoCode: t.boolean,
    operatorIsPickup: t.boolean,
    splitTimeCost: t.boolean,
    abbr: t.string,
    defaultDispatchRequestDuration: t.number,
    regionList: t.array(t.string),

    objectPreferences: ObjectPreferenceArrayCodec,
    waterGoPreferences: WaterGoPreferenceCodec,
    oilGoPreference: OilGoPreferenceCodec,
  })
]);

/** Validator for service type array types. */
export const ServiceTypeArrayValidator = t.array(ServiceTypeValidator);

export type ServiceType = t.TypeOf<typeof ServiceTypeValidator>;
export type ServiceTypeArray = t.TypeOf<typeof ServiceTypeArrayValidator>;

/**
 * A service type.
 */
@Exclude()
export class ServiceTypeEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  private type!: string;

  @Expose()
  description?: string;

  @Expose()
  operatorIsPickup?: boolean;

  @Expose()
  splitTimeCost?: boolean;

  @Expose()
  abbr?: string;

  @Expose()
  defaultDispatchRequestDuration?: number;

  @Expose()
  category?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toProductionAllocationMethod))
  productionAllocationMethod?: ProductionAllocationMethod;

  @Expose()
  autoCode?: boolean

  @Expose()
  doNotUse = false;

  @Expose({name: "regionList"})
  regionIds?: string[];

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[]

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity

  readonly entityType = "SERVICETYPE";

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for ServiceTypeEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ServiceType): ServiceTypeEntity {
    return plainToInstance(ServiceTypeEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ServiceType> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ServiceTypeValidator, object);
  }

  asTypeValue(): ServiceType {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ServiceTypeValidator, object);
  }

  get name(): string {
    return this.type;
  }

  set name(name: string) {
    this.type = name;
  }
}

export const SERVICE_TYPE_PATCH_FIELD_MAP: Record<string, keyof ServiceTypePatchDTO> = {
  type: 'type',
  description: 'description',
  abbr: 'abbr',
  splitTimeCost: 'splitTimeCost',
  category: 'category',
  defaultDispatchRequestDuration: 'defaultDispatchRequestDuration',
  productionAllocationMethod: 'productionAllocationMethod',
  autoCode: 'autoCode',
  doNotUse: 'doNotUse',
};

export interface ServiceTypePatchDTO {
  type?: PatchValue<string>
  description?: PatchValue<string>
  abbr?: PatchValue<string>
  operatorIsPickup?: PatchValue<boolean>
  splitTimeCost?: PatchValue<boolean>
  category?: PatchValue<string>
  defaultDispatchRequestDuration?: PatchValue<number>
  productionAllocationMethod?: PatchValue<string>
  autoCode?: PatchValue<boolean>
  doNotUse?: PatchValue<boolean>
}
